/* eslint-disable react-hooks/exhaustive-deps,import/no-unresolved */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Redirect, useHistory, useRouteMatch } from 'react-router-dom';
import PromoImage from 'assets/images/staking-promo-new.png';
import { currencies } from '../../../utilities/constants';
import { showAlert } from '../../../utilities/notifications';
import { useLedgerFilteredWallets } from '../../../utilities/sharedHooks/ledgerWallets';
import { USER_CREATE_WALLET } from '../../Accounts/mutations';
import { WALLETS_ALL } from '../../Accounts/queries';
import StakingLayout from '../../shared/StakingLayout';
import { featuresScreens, mainStakingCurrency, stakingTypes } from '../constants';
import { GET_ALL_STAKINGS } from '../queries';
import { useUserAllowStakingFeature } from '../../../utilities/sharedHooks/user';
import { SkeletonPageLoader } from '../../../components';

const StakingPromo = () => {
  const { t: translate } = useTranslation();
  const { path } = useRouteMatch();
  const history = useHistory();

  const queryClient = useQueryClient();

  const [showStakingBXXBenefitsIframe, setShowStakingBXXBenefitsIframe] = useState(false);

  const promoTitle = translate('staking_promo_title');
  const promoSubtitle = translate('staking_promo_subtitle');

  const isAllowStakingFeature = useUserAllowStakingFeature();

  useEffect(() => {
    if (isAllowStakingFeature === false) {
      history.push(`/`);
    }
  }, [isAllowStakingFeature]);

  const {
    data: wallets,
    refetch: refetchAllWallets,
    isLoading: isLoadingFetchWallets,
  } = useLedgerFilteredWallets(WALLETS_ALL);

  const {
    data: allStakings,
    refetch: refetchAllStaking,
    isLoading: isLoadingFetchStakings,
  } = useQuery(GET_ALL_STAKINGS, {
    enabled: false,
  });

  const { mutate: createWallets, isLoading: isCreatingWallets } = useMutation(USER_CREATE_WALLET, {
    onSettled: (data, error) => {
      if (!error) {
        if (data && data[mainStakingCurrency]) {
          const { error: walletError } = data[mainStakingCurrency];
          if (walletError) {
            if (walletError.error) {
              showAlert({
                message: walletError.error.message,
                type: 'danger',
              });
            } else {
              showAlert({ message: walletError, type: 'danger' });
            }
          } else {
            history.push('/dashboard/staking/features', {
              initScreen: featuresScreens.ADD_FUNDS_OPTIONS,
            });
            queryClient.resetQueries(WALLETS_ALL);
          }
        }
      } else if (error) {
        showAlert({ message: error.message, type: 'danger' });
      }
    },
  });

  useEffect(() => {
    refetchAllWallets();
    refetchAllStaking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mainStakingWallet = useMemo(() => {
    if (wallets) {
      return wallets?.find((w) => w.coin.toLowerCase() === mainStakingCurrency);
    }
    return {};
  }, [wallets]);

  const bxxAvailableBalance =
    mainStakingWallet?.available_balance *
    currencies[mainStakingCurrency?.toUpperCase()].conversion;

  const openStakingFlow = bxxAvailableBalance > 0;

  if (isLoadingFetchWallets || isLoadingFetchStakings) {
    return <SkeletonPageLoader />;
  }

  if (allStakings && allStakings?.length > 0) {
    return <Redirect to={`${path}${stakingTypes.ACTIVE}`} />;
  }

  const stakingNowHandler = () => {
    if (openStakingFlow) {
      history.push('/dashboard/staking/features', {
        initScreen: featuresScreens.CREATE_STAKING,
      });
    } else if (mainStakingWallet && mainStakingWallet?.id) {
      history.push('/dashboard/staking/features', {
        initScreen: featuresScreens.ADD_FUNDS_OPTIONS,
      });
    } else {
      createWallets({
        wallets: [mainStakingCurrency],
      });
    }
  };

  const openIframe = () => {
    setShowStakingBXXBenefitsIframe(true);
  };

  const stakingBenefits = [
    {
      id: 1,
      mainLabel: 'staking_benefit_1_main_label',
      secondaryLabel: 'staking_benefit_1_secondary_label',
    },
    {
      id: 2,
      mainLabel: 'staking_benefit_2_main_label',
      secondaryLabel: 'staking_benefit_2_secondary_label',
    },
    {
      id: 3,
      mainLabel: 'staking_benefit_3_main_label',
      secondaryLabel: 'staking_benefit_3_secondary_label',
    },
  ];

  return (
    <StakingLayout>
      <div className="staking-promo-container">
        <div className="d-flex flex-column pt-4 flex-md-row align-items-md-center justify-content-md-between pt-md-0">
          <img
            className="w-100 pe-md-5"
            style={{ width: '20rem', height: '20rem', objectFit: 'contain' }}
            src={PromoImage}
            alt="no-staking"
          />
          <div>
            <h1 className="promo-title staking-promo-container-title mb-4 w-md-100 text-uppercase mt-4">
              {promoTitle}
            </h1>
            <p className="text-dark m-0 mb-5 text-left w-100 d-block me-2 text-f5">
              {promoSubtitle}
            </p>
            <div className="w-100 d-flex flex-row">
              <div className="w-100">
                <button
                  type="button"
                  className="btn btn-primary py-3 w-100"
                  loading={isCreatingWallets ? 'true' : 'false'}
                  disabled={isCreatingWallets}
                  onClick={stakingNowHandler}>
                  {translate('stake_now_btn_label')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-3 mt-4 px-3 px-md-0">
        <p className="text-dark text-f4 text-bold">{translate('staking_benefits_title')}</p>
        <div className="mt-4 d-flex flex-column flex-md-row align-items-md-center justify-content-md-between">
          {stakingBenefits.map((benefit, index) => (
            <div
              className={`d-flex flex-column algin-items-start ${
                index !== 0 ? 'mt-4 mt-md-0' : ''
              }`}
              key={benefit.id}>
              <div className="d-flex flex-row justify-content-start align-items-start">
                <div className="bg-primary px-2 rounded-max">
                  <p className="text-f5 text-dark">{benefit.id}</p>
                </div>
                <div className="ms-3 pe-4">
                  {/* browser layout */}
                  <p className="text-dark text-f5 d-none d-md-block w-80">
                    {translate(benefit.mainLabel)}
                  </p>
                  {/* mobile layout */}
                  <p className="text-dark text-f d-block d-md-none w-100">
                    {translate(benefit.mainLabel)}
                  </p>
                  <p className="text-shadow text-f5 mt-2 mt-md-3">
                    {translate(benefit.secondaryLabel)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 mb-5 mt-md-5 pt-md-0 mb-md-0">
          <button
            onClick={() => {
              openIframe();
            }}
            type="button"
            className="btn text-f5 text-primary rounded-3 p-0 m-0 text-nowrap d-flex flex-row text-btn-item border-0">
            {`${translate('learn_more_btn_label')}`}{' '}
            <div className="ms-2" style={{ transform: 'rotate(180deg)' }}>
              <i className="icon icon-arrow_back-navigation" aria-label="Back" />
            </div>
          </button>
        </div>
      </div>

      {showStakingBXXBenefitsIframe && (
        <div className="w-100 h-100 position-fixed start-0 top-0 bg-light" style={{ zIndex: 999 }}>
          <span>
            <i
              onClick={() => {
                setShowStakingBXXBenefitsIframe(false);
              }}
              onKeyDown={() => {}}
              role="button"
              className="close-btn p-2 bg-shadow position-absolute me-5 mt-2 end-0 top-0 cursor-pointer icon icon-close-other text-white"
              tabIndex={-1}
              aria-label="Close"
            />
          </span>
          <iframe src="https://withcl.com/bxx" title="Staking Bxx" width="100%" height="100%" />
        </div>
      )}
    </StakingLayout>
  );
};

export default StakingPromo;
